import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../components/form-control/Input';
import Button from '../../../components/form-control/Button';
import { ICreateVehicleVerificationDTO } from '../../../resources/interfaces';
import { useEffect, useState } from 'react';
import { VEHICLE_COVER_TYPES } from '../../../resources/constants';
import SelectMenu, { ISelectMenuItemValue } from '../../../components/form-control/SelectMenu';
import { getFormattedPhoneNumber, getListFromRecord, getPhoneNumberFromShortCodeFormat, getCoverTypeKeyFromString, getSelectableManufacturingYears, getVehicleCoverTypeFromKey, showToast } from '../../../utils/miscFunctions';
import { useQuery } from '@tanstack/react-query';
import { createVehicleVerification, getVehicleBodyTypes, searchVehicleMake, searchVehicleModelsByMake, updateVehicleVerification } from '../../../api/requests';
import { MultiSelect, IMultiSelectItem } from '../../../components/form-control/MultiSelect';
import SelectDropdown, { ISelectDropdownItem } from '../../../components/form-control/SelectDropdown';
import InputSelectMenu from '../../../components/form-control/InputSelectMenu';
import { VerifiedVehicle } from '../../../models/verifiedVehicle/VerifiedVehicle';
import IntlPhoneInput from '../../../components/form-control/IntlPhoneInput/IntlPhoneInput';
import useUserAccount from '../../../hooks/userAccountHook';
import { useDispatch, useSelector } from 'react-redux';
import { getInsurersFromState, UPDATE_INSURERS } from '../../../states/slices/resourceSlice';
import FileUpload from '../../../components/elements/FileUpload/FileUpload';
import { VERIFICATION_EVENT_TYPES } from '../../../resources/enums';
import { useNavigate } from 'react-router-dom';
import useRoute from '../../../hooks/routesHook';

type IVerifiactionType = 'vehicle' | 'fleet';
interface IVehicleVerificationProps {
    onClose: () => void;
    onProceed: () => void;
    vehicleInfo?: VerifiedVehicle;
    isEdit?: boolean;
    type?: IVerifiactionType;
}
interface IFormData {
    fullName: string;
    phone: string;
    make: string;
    model: string;
    registration: string;
    insurerId?: string;
    eventType?: VERIFICATION_EVENT_TYPES;
}

const CreateVerification = ({ onClose, onProceed, vehicleInfo, isEdit, type }: IVehicleVerificationProps): JSX.Element => {
    const { user } = useUserAccount();
    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [vehicleModels, setVehicleModels] = useState<any>([]);
    const [vehicleMakePrefix, setVehicleMakePrefix] = useState<string>("");
    const [vehicleMakes, setVehicleMakes] = useState<any>([]);
    const [selectedVehicleMake, setSelectedVehicleMake] = useState<string>("");
    const [manufacturedYrList] = useState<Array<number>>(getSelectableManufacturingYears());
    const COVER_TYPES = getListFromRecord(VEHICLE_COVER_TYPES, 'title').filter((type) => type.includes('Comprehensive'));
    const [loadingMakes, setLoadingMakes] = useState<boolean>(false);
    const [modelsSearchQuery, setModelsSearchQuery] = useState<string>("");
    const [vehicleBodyTypes, setVehicleBodyTypes] = useState<any>([]);
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const insurers: ISelectDropdownItem[] = useSelector(getInsurersFromState)?.map((insurer: any) => ({
        name: insurer?.name ?? "",
        value: String(insurer.id),
    })) ?? [];
    const [csvFile, setCsvFile] = useState<File | null>(null);

    const fetchVehicleBodyTypes = async () => {
        try {
            const response = await getVehicleBodyTypes();
            if (response) {
                setVehicleBodyTypes(response?.vehicleTypes);
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    useQuery({
        queryKey: ['vehicle-body-types'],
        queryFn: fetchVehicleBodyTypes,
    })

    useEffect(() => {
        if (selectedVehicleMake) {
            setVehicleModels([]);
            seachForVehicleModel();
        }
    }, [selectedVehicleMake]);

    useEffect(() => {
        setLoadingMakes(true);
        seachForVehicleMake();
    }, [vehicleMakePrefix]);

    const seachForVehicleModel = async () => {
        try {
            if (selectedVehicleMake) {
                let models = await searchVehicleModelsByMake(selectedVehicleMake?.toUpperCase()?.trim());
                if (models?.data) {
                    setVehicleModels(models?.data);
                }
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    const seachForVehicleMake = async () => {
        try {
            let makes = await searchVehicleMake(vehicleMakePrefix?.toUpperCase());
            if (makes) {
                setVehicleMakes(makes?.data?.splice(0, 100));
                setTimeout(() => {
                    setLoadingMakes(false);
                }, 3000);
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    const handleSubmit = async (values: IFormData) => {
        const insurerId = user.isInsurer() ? null : Number(values.insurerId);

        setSubmitting(true);
        const payload: ICreateVehicleVerificationDTO = {
            fullName: values.fullName,
            inspectionVehicles: [{
                make: values.make,
                model: values.model,
                registration: values.registration,
            }],
            phone: getFormattedPhoneNumber(values.phone),
            insurerId: insurerId,
            eventType: values.eventType,
        };


        if (type === 'vehicle') {
          
            try {
                const response: any = isEdit && vehicleInfo ? await updateVehicleVerification(vehicleInfo?.id, payload) : await createVehicleVerification(payload)

                if (response) {
                    showToast.success(response?.message ?? isEdit ? 'Vehicle verification updated successfully' : 'Vehicle verification created successfully');
                    onProceed();
                    onClose();
                    navigate(`${getDashboardBase()}/vehicle-verification/${response.applicationNumber}`)
                }
            }
            catch (error) {
                console.log(error)
            }
            finally {
                setSubmitting(false);
            }
        }

    }

    return (
        <div className="py-6 px-3 md:px-6 w-full ">
            <Formik
                initialValues={{
                    fullName: vehicleInfo?.customer?.fullName ?? "",
                    phone: getPhoneNumberFromShortCodeFormat(vehicleInfo?.customer?.phone ?? ""),
                    make: vehicleInfo?.vehicles?.[0].make ?? "",
                    model: vehicleInfo?.vehicles?.[0].model ?? "",
                    registration: vehicleInfo?.vehicles?.[0].registrationNumber ?? "",
                    insurerId: vehicleInfo?.insurer?.id ?? undefined,
                    eventType: vehicleInfo?.eventType ?? undefined,
                }}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={Yup.object().shape({
                    fullName: Yup.string()
                    .required('Full name is required.')
                    .test(
                    'full-name-format',
                    'Full name must contain at least two words.',
                    value => !!value && value.trim().split(/\s+/).length >= 2
                    ),
                    phone: Yup.string().required('Phone is required.'),
                    make: Yup.string().required('Make is required.'),
                    model: Yup.string().required('Model is required.'),
                    registration: Yup.string().required('Registration Number is required.'),
                    insurerId: !user.isInsurer() ? Yup.string().required('Insurer is required.') : Yup.string(),
                    eventType: Yup.string().required('Type is required.'),
                })}

                onSubmit={handleSubmit}>
                {({ values, errors, isSubmitting, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => {
                    return (
                        <form className="" onSubmit={handleSubmit}>
                            <div className="w-full flex flex-col gap-5">
                                <div className="border border-gray-200 rounded-lg p-5 dark:bg-darkBg dark:border dark:border-darkBg-border">
                                    <h3 className="text-lg font-medium mb-3 dark:text-darkBg-mainText">Personal Information</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <Input
                                            type={'text'}
                                            name="fullName"
                                            value={values.fullName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Full Name"
                                            placeholder="Enter full name"
                                            required
                                            error={touched.fullName ? errors.fullName : undefined}
                                            helperText={touched?.fullName ? errors.fullName : undefined}
                                            className="rounded"
                                        />

                                        <IntlPhoneInput
                                            value={values.phone}
                                            onChange={(value) => setFieldValue('phone', value)}
                                            onBlur={handleBlur}
                                            label="Mobile Number"
                                            placeholder="Enter mobile number"
                                            error={touched.phone ? errors.phone : undefined}
                                        />

                                    </div>
                                </div>

                                {type === 'vehicle' && (
                                    <div className="border border-gray-200 rounded-lg p-5 dark:bg-darkBg dark:border dark:border-darkBg-border">
                                        <h3 className="text-lg font-medium mb-3 dark:text-darkBg-mainText">Vehicle Information</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <MultiSelect
                                                label="Vehicle Make"
                                                selected={[values.make]}
                                                onChange={(val: string[]) => {
                                                    setFieldValue("make", val?.[0]);
                                                    if (val && val.length > 0) {
                                                        setSelectedVehicleMake(val[0]);
                                                    }
                                                }}
                                                list={vehicleMakes?.map(
                                                    (make: any) =>
                                                    ({
                                                        name: make,
                                                        value: make,
                                                    } as IMultiSelectItem)
                                                )}
                                                placeholder="Search and select your vehicle make"
                                                selectInputClassName='!shadow-none'
                                                error={touched.make ? errors.make : undefined}
                                                helperText={touched.make ? errors.make : undefined}
                                                required
                                                description="vehicle make - manufacturer"
                                                onQueryChange={(query: string) => { setVehicleMakePrefix(query) }}
                                                multiSelect={false}
                                                loading={loadingMakes}
                                            />

                                            <InputSelectMenu
                                                name={'model'}
                                                label="Vehicle Model"
                                                placeholder="Select your vehicle model"
                                                description="vehicle model - type"
                                                list={vehicleModels}
                                                listSelectedValue={values.model}
                                                onChange={(val: string) => {
                                                    setFieldValue("model", val);
                                                }}
                                                error={touched.model ? errors.model : undefined}
                                                helperText={touched.model ? errors.model : undefined}
                                                required
                                                search
                                                searchPlaceHolder="Enter your vehicle model. Eg. Yaris"
                                                sort
                                            />

                                            <Input
                                                type={'text'}
                                                name="registration"
                                                value={values.registration?.toUpperCase()}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Registration Number"
                                                placeholder="Enter registration number"
                                                required
                                                error={touched.registration ? errors.registration : undefined}
                                                helperText={touched?.registration ? errors.registration : undefined}
                                                className="rounded"
                                            />

                                            <SelectMenu
                                                label="Inspection Type"
                                                name="eventType"
                                                listSelectedValue={values.eventType}
                                                onChange={(val: ISelectMenuItemValue | null) => {
                                                    setFieldValue('eventType', val);
                                                }}
                                                onBlur={handleBlur}
                                                list={['POLICY', 'CLAIM']}
                                                placeholder="Select a type"
                                                required
                                                error={touched.eventType ? errors.eventType : undefined}
                                                helperText={touched?.eventType ? errors.eventType : undefined}
                                            />


                                        </div>

                                        <div className="mt-5">
                                            {!user.isInsurer() && (
                                                <SelectDropdown
                                                    label="Select Insurer"
                                                    name="insurerId"
                                                    listSelectedValue={values.insurerId}
                                                    onChange={(val: string | null) => {
                                                        setFieldValue("insurerId", val);
                                                    }}
                                                    onBlur={handleBlur}
                                                    list={insurers}
                                                    search
                                                    searchPlaceHolder="Enter insurer name"
                                                    placeholder="Select Insurer"
                                                    error={touched.insurerId ? errors.insurerId : undefined}
                                                    helperText={touched?.insurerId ? errors.insurerId : undefined}
                                                    required
                                                    emptyListMessage="Select insurer to proceed"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* {type === 'fleet' && (
                                    <div className="border border-gray-200 rounded-lg p-5">
                                        <h3 className="text-lg font-medium mb-3">Fleet Information</h3>

                                        <form onSubmit={handleSubmit}>
                                            <FileUpload
                                                type="csv"
                                                label="Upload fleet datasheet"
                                                label2="Download template"
                                                onFileLoad={(images: any) => {
                                                    if (images && images[0]) {
                                                        setCsvFile(images[0]);
                                                    }
                                                }}
                                                showFileMax={false}
                                                onFileDeleted={() => {
                                                    setCsvFile(null);
                                                }}
                                                defaultImage={''}
                                                cropImage={false}
                                                maxHeight={90}
                                                maxWidth={200}
                                                imageContainerHeight={'200px'}
                                                imageAlt={'CSV File'}
                                            />
                                        </form>
                                    </div>
                                )} */}
                            </div>

                            <div className="w-full flex gap-4 mt-4">
                                <Button
                                    type="button"
                                    label="Cancel"
                                    onClick={onClose}
                                    bgColor='secondary'
                  fullWidth

                                />
                                <Button
                                    type="submit"
                                    label="Save"
                                    bgColor='primary'
              fullWidth
                                    disabled={submitting}
                                    loading={submitting}
                                />
                            </div>
                        </form>
                    )
                }}
            </Formik>
        </div>
    );
};

export default CreateVerification;
